<script setup lang="ts">
import { Separator } from '@/components/ui/separator'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { useForm } from 'vee-validate'
import { defineEmits, ref } from 'vue'
import LoginCard from '@/views/auth/components/LoginCard.vue'
import { AuthApi } from '@/services/api/auth.ts'
import { AuthService } from '@/services/auth.ts'
import axios from 'axios'
import { Loader2 } from 'lucide-vue-next'
import GoogleLogin from '@/views/auth/components/GoogleLogin.vue'
import PasswordInput from '@/components/revio/input/PasswordInput.vue'

const emit = defineEmits<{
  (event: 'success', values: { tempAuthToken: string; otpUseEmail: boolean }): void
  (event: 'forgot-password'): void
  (event: 'login-with-okta'): void
  (event: 'login-with-entra'): void
}>()

const formSchema = toTypedSchema(
  z.object({
    email: z.string().email('Wrong email format').min(1, 'Email is required'),
    password: z.string().min(1, 'Password is required'),
    remember: z.boolean().default(true),
  }),
)

const form = useForm({
  validationSchema: formSchema,
})

const isLoading = ref<boolean>(false)
const hasError = ref<boolean>(false)

const onSubmit = form.handleSubmit(async (values) => {
  try {
    isLoading.value = true
    const response = await AuthApi.loginEmailPassword(values)
    AuthService.shouldRemember(values.remember)
    isLoading.value = false
    emit('success', {
      tempAuthToken: response.temp_auth_token,
      otpUseEmail: response.otp_use_email,
    })
  } catch (error) {
    if (axios.isAxiosError<401>(error)) {
      hasError.value = true
    }
    // TODO: Display error message on the ui
    console.error('Error:', error)
  }
  isLoading.value = false
})
</script>

<template>
  <LoginCard>
    <form @submit="onSubmit">
      <CardHeader>
        <img
          src="/src/assets/media/revio/revio.svg"
          class="shrink-0 h-6 text-center p-0 m-0 mb-2.5"
          alt="Revio"
        />
        <CardTitle class="text-center">Welcome</CardTitle>
        <CardDescription class="text-center">Please sign in to continue</CardDescription>
      </CardHeader>

      <div class="grid gap-3 max-w-[324px] mx-auto">
        <GoogleLogin />
        <Button variant="outline" class="h-10 shadow-none border-[#dadce0] rounded-sm text-[#3c4043]" @click="emit('login-with-okta')">
          <img src="/src/assets/media/brand-logos/okta.svg" alt="Okta" class="size-[22px]" />
          Sign in with Okta
        </Button>
        <Button variant="outline" class="h-10 shadow-none border-[#dadce0] rounded-sm text-[#3c4043]" @click="emit('login-with-entra')">
          <img src="/src/assets/media/brand-logos/microsoft-entra.svg" alt="Entra ID" class="size-[22px]" />
          Sign in with Entra ID
        </Button>
      </div>

      <Separator class="mt-6 mb-6" label="OR" />

      <div
        v-if="hasError"
        class="mb-4 p-3 text-red-700 bg-red-100 border border-red-400 rounded-lg"
      >
        Invalid email or password
      </div>

      <FormField v-slot="{ componentField }" name="email">
        <FormItem class="mb-4">
          <FormLabel>Email</FormLabel>
          <FormControl>
            <Input id="email" type="email" v-bind="componentField" placeholder="Enter email" @keydown.enter="onSubmit"/>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField }" name="password">
        <FormItem class="mb-4">
          <FormLabel>Password</FormLabel>
          <FormControl>
            <PasswordInput id="password" v-bind="componentField" placeholder="Enter Password" @keydown.enter="onSubmit"/>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <div class="flex justify-between items-center">
        <FormField v-slot="{ value, handleChange }" type="checkbox" name="remember">
          <FormItem class="space-y-0.5 flex items-center">
            <FormControl>
              <Checkbox
                id="remember"
                class="mr-2"
                :checked="value"
                @update:checked="handleChange"
              />
              <Label for="remember">Remember me</Label>
            </FormControl>
          </FormItem>
        </FormField>

        <Button variant="ghost" size="sm" @click.prevent="emit('forgot-password')">
          Forgot Password?
        </Button>
      </div>

      <Button class="mt-4 w-full" type="submit" :disabled="isLoading">
        <Loader2 v-if="isLoading" class="size-4 mr-2 animate-spin" />
        Sign In
      </Button>
    </form>
  </LoginCard>
</template>

<style scoped></style>
